import { useState, useEffect, useCallback } from "react";

// ============================================================================
// brioCAST — Fleet Dashboard (Partner Multi-Venue Management)
// ============================================================================
// Nav item:  { id: "fleet", label: "場域管理", icon: "🏢", cap: "PARTNER" }
// Route:     case "fleet": return hasCap(account, "PARTNER") ? <FleetDashboard /> : null;
// API:       /api/account/delegations/*
// ============================================================================

const API_BASE = "https://api.briolabs.io/api/account";

async function fleetApi(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Request failed");
  return json;
}

// Theme (matches BusinessPortal dark theme)
const t = {
  bg: "#0F1117", card: "#1A1D27", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE", accentBg: "rgba(108,92,231,0.12)",
  success: "#00B894", successBg: "rgba(0,184,148,0.12)",
  warning: "#FDCB6E", warningBg: "rgba(253,203,110,0.15)",
  danger: "#E17055", dangerBg: "rgba(225,112,85,0.12)",
  text: "#E8E8ED", muted: "#8B8FA3", dim: "#5A5E72",
  mono: "'JetBrains Mono', 'SF Mono', monospace",
};

const PERM_LABELS = {
  device_management: { label: "設備管理", icon: "📺" },
  content_schedule: { label: "內容排程", icon: "📋" },
  view_revenue: { label: "收益查看", icon: "👁️" },
  manage_revenue: { label: "收益管理", icon: "💰" },
  view_disputes: { label: "爭議查看", icon: "🛡️" },
  manage_settings: { label: "帳號設定", icon: "⚙️" },
};

function PermBadge({ perm, active }) {
  const meta = PERM_LABELS[perm] || { label: perm, icon: "🔧" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600,
      color: active ? t.success : t.dim,
      background: active ? t.successBg : `${t.dim}15`,
    }}>{meta.icon} {meta.label}</span>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "啟用中", color: t.success, bg: t.successBg },
    pending: { label: "待接受", color: t.warning, bg: t.warningBg },
    revoked: { label: "已撤銷", color: t.danger, bg: t.dangerBg },
    expired: { label: "已過期", color: t.dim, bg: `${t.dim}15` },
  };
  const s = map[status] || map.active;
  return (
    <span style={{
      display: "inline-flex", padding: "3px 10px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, color: s.color, background: s.bg,
    }}>{s.label}</span>
  );
}

// ── Invite Modal ──
function InviteModal({ onClose, onInvite }) {
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [perms, setPerms] = useState({
    device_management: true,
    content_schedule: true,
    view_revenue: true,
    manage_revenue: false,
    view_disputes: false,
    manage_settings: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const togglePerm = (key) => setPerms({ ...perms, [key]: !perms[key] });

  const submit = async () => {
    if (!email.includes("@")) { setError("請輸入有效 Email"); return; }
    setBusy(true);
    setError(null);
    try {
      const activePerms = {};
      Object.entries(perms).forEach(([k, v]) => { if (v) activePerms[k] = true; });
      await onInvite({ email, label: label || undefined, permissions: activePerms });
      onClose();
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.6)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={onClose}>
      <div style={{
        background: t.card, borderRadius: 16, padding: 24, width: 440,
        border: `1px solid ${t.border}`, boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 20px", fontSize: 18, fontWeight: 700, color: t.text }}>
          邀請管理者
        </h3>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: "block", marginBottom: 4 }}>
            受邀者 Email *
          </label>
          <input
            value={email} onChange={e => setEmail(e.target.value)}
            placeholder="partner@example.com"
            style={{
              width: "100%", padding: "10px 14px", borderRadius: 8,
              border: `1.5px solid ${t.border}`, background: t.bg, color: t.text,
              fontSize: 14, boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: "block", marginBottom: 4 }}>
            標籤 / 備註
          </label>
          <input
            value={label} onChange={e => setLabel(e.target.value)}
            placeholder="副都心咖啡 - 總店"
            style={{
              width: "100%", padding: "10px 14px", borderRadius: 8,
              border: `1.5px solid ${t.border}`, background: t.bg, color: t.text,
              fontSize: 14, boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: t.muted, display: "block", marginBottom: 8 }}>
            授權範圍
          </label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {Object.entries(PERM_LABELS).map(([key, meta]) => (
              <button key={key} onClick={() => togglePerm(key)} style={{
                display: "flex", alignItems: "center", gap: 4,
                padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${perms[key] ? t.success : t.border}`,
                background: perms[key] ? t.successBg : "transparent",
                color: perms[key] ? t.success : t.muted,
                cursor: "pointer", transition: "all 0.15s",
              }}>
                {meta.icon} {meta.label}
                {perms[key] && <span style={{ marginLeft: 2 }}>✓</span>}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div style={{
            padding: "8px 12px", borderRadius: 8, marginBottom: 16,
            background: t.dangerBg, color: t.danger, fontSize: 12,
          }}>{error}</div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={submit} disabled={busy} style={{
            flex: 1, padding: "12px", borderRadius: 10, border: "none",
            background: t.accent, color: "#fff", fontSize: 14, fontWeight: 700,
            cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
          }}>{busy ? "處理中..." : "發送邀請"}</button>
          <button onClick={onClose} style={{
            padding: "12px 20px", borderRadius: 10, background: "none",
            border: `1.5px solid ${t.border}`, color: t.muted, fontSize: 14, cursor: "pointer",
          }}>取消</button>
        </div>
      </div>
    </div>
  );
}

// ── Venue Detail Panel ──
function VenueDetail({ delegation, onBack, onRevoke }) {
  const [devices, setDevices] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const perms = JSON.parse(delegation.permissions || "{}");

  const load = useCallback(async () => {
    try {
      if (perms.device_management) {
        const d = await fleetApi(`/delegations/${delegation.id}/devices`);
        setDevices(d.data.devices || []);
      }
      if (perms.view_revenue || perms.manage_revenue) {
        const r = await fleetApi(`/delegations/${delegation.id}/revenue`);
        setRevenue(r.data);
      }
    } catch (e) {
      setToast(e.message);
    }
    setLoading(false);
  }, [delegation.id, perms.device_management, perms.manage_revenue, perms.view_revenue]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (toast) { const id = setTimeout(() => setToast(null), 3000); return () => clearTimeout(id); } }, [toast]);

  const handleRevoke = async () => {
    if (!window.confirm("確定要撤銷此場域的管理權限？")) return;
    try {
      await onRevoke(delegation.id);
    } catch (e) {
      setToast(e.message);
    }
  };

  if (loading) return <div style={{ padding: 60, color: t.dim, textAlign: "center" }}>載入中...</div>;

  return (
    <div>
      {toast && (
        <div style={{
          position: "fixed", top: 16, right: 16, zIndex: 999, padding: "12px 20px",
          borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
          background: t.dangerBg, color: t.danger, boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }} onClick={() => setToast(null)}>{toast}</div>
      )}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: t.muted, fontSize: 22, cursor: "pointer" }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: t.text }}>
            {delegation.venue_name || delegation.label || "場域"}
          </div>
          <div style={{ fontSize: 12, color: t.dim, fontFamily: t.mono }}>
            {delegation.venue_email} · {delegation.grantor_account_id || delegation.grantee_account_id}
          </div>
        </div>
        <StatusBadge status={delegation.status} />
      </div>

      {/* Permissions */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
        {Object.keys(PERM_LABELS).map(k => (
          <PermBadge key={k} perm={k} active={!!perms[k]} />
        ))}
      </div>

      {/* Devices */}
      {perms.device_management && (
        <div style={{ background: t.card, borderRadius: 12, padding: 16, border: `1px solid ${t.border}`, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.dim, letterSpacing: 0.5, marginBottom: 12 }}>
            📺 設備 ({devices.length})
          </div>
          {devices.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: t.dim, fontSize: 13 }}>尚無設備</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {devices.map(d => (
                <div key={d.device_id} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                  borderRadius: 8, background: t.bg, border: `1px solid ${t.border}`,
                }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: d.status === "active" ? t.success : t.danger,
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: t.dim }}>{d.location || d.scene_type} · {d.device_id}</div>
                  </div>
                  <div style={{ fontSize: 11, color: d.screen_state === "on" ? t.success : t.dim }}>
                    {d.screen_state === "on" ? "🟢 在線" : "⚫ 離線"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Revenue */}
      {(perms.view_revenue || perms.manage_revenue) && revenue && (
        <div style={{ background: t.card, borderRadius: 12, padding: 16, border: `1px solid ${t.border}`, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.dim, letterSpacing: 0.5, marginBottom: 12 }}>
            💰 收益
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: t.success, fontFamily: t.mono }}>
            NT${Math.round(revenue.total_revenue).toLocaleString()}
          </div>
          <div style={{ fontSize: 12, color: t.dim, marginTop: 4 }}>累計收益</div>
          {revenue.earnings && revenue.earnings.length > 0 && (
            <div style={{ marginTop: 12, maxHeight: 200, overflowY: "auto" }}>
              {revenue.earnings.slice(0, 10).map((e, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", padding: "6px 0",
                  borderBottom: `1px solid ${t.border}33`, fontSize: 12,
                }}>
                  <span style={{ color: t.muted, fontFamily: t.mono }}>{e.date}</span>
                  <span style={{ color: t.text }}>
                    {e.impressions} imp · <span style={{ color: t.success, fontWeight: 600 }}>NT${e.revenue_ntd}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Revoke */}
      <button onClick={handleRevoke} style={{
        padding: "10px 20px", borderRadius: 10, background: "transparent",
        border: `1.5px solid ${t.danger}44`, color: t.danger, fontSize: 13,
        cursor: "pointer", transition: "all 0.15s",
      }}>撤銷管理權限</button>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================
export default function FleetDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fleetApi("/delegations");
      setData(r.data);
    } catch (e) {
      setToast(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (toast) { const id = setTimeout(() => setToast(null), 3000); return () => clearTimeout(id); } }, [toast]);

  const handleInvite = async (form) => {
    const r = await fleetApi("/delegations/invite", { method: "POST", body: form });
    setToast(r.data.message);
    load();
  };

  const handleRevoke = async (id) => {
    await fleetApi(`/delegations/${id}`, { method: "DELETE" });
    setToast("已撤銷管理權限");
    setSelectedVenue(null);
    load();
  };

  if (loading) return <div style={{ padding: 60, color: t.dim, textAlign: "center" }}>載入中...</div>;

  if (selectedVenue) {
    return (
      <div>
        <Header />
        <VenueDetail
          delegation={selectedVenue}
          onBack={() => setSelectedVenue(null)}
          onRevoke={handleRevoke}
        />
      </div>
    );
  }

  const managed = data?.managed_venues || [];
  const delegated = data?.delegated_to || [];
  const pending = data?.pending_invites || [];
  const allVenues = [...managed, ...delegated];

  return (
    <div>
      {toast && (
        <div style={{
          position: "fixed", top: 16, right: 16, zIndex: 999, padding: "12px 20px",
          borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer",
          background: t.successBg, color: t.success, boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }} onClick={() => setToast(null)}>{toast}</div>
      )}

      <Header />

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onInvite={handleInvite} />}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "管理中場域", value: allVenues.filter(v => v.status === "active").length, icon: "🏢", color: t.accent },
          { label: "已授權", value: delegated.filter(v => v.status === "active").length, icon: "🤝", color: t.success },
          { label: "待接受邀請", value: pending.length, icon: "📩", color: t.warning },
        ].map(c => (
          <div key={c.label} style={{ background: t.card, borderRadius: 10, padding: "14px 16px", border: `1px solid ${t.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.muted }}>{c.label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: c.color, fontFamily: t.mono, marginTop: 2 }}>{c.value}</div>
              </div>
              <div style={{ fontSize: 20, opacity: 0.3 }}>{c.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Invite button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.text }}>場域清單</h3>
        <button onClick={() => setShowInvite(true)} style={{
          padding: "8px 16px", borderRadius: 8, border: "none",
          background: t.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>+ 邀請管理</button>
      </div>

      {/* Managed Venues (I'm grantee — venues I manage) */}
      {managed.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.dim, marginBottom: 8 }}>我管理的場域</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {managed.map(v => (
              <div key={v.id} onClick={() => setSelectedVenue(v)} style={{
                background: t.card, borderRadius: 10, padding: "14px 18px", cursor: "pointer",
                border: `1px solid ${t.border}`, transition: "border-color 0.15s",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: t.accentBg,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                  }}>🏢</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>
                      {v.venue_name || v.label || "場域"}
                    </div>
                    <div style={{ fontSize: 11, color: t.muted }}>{v.venue_email}</div>
                  </div>
                  <StatusBadge status={v.status} />
                  <span style={{ color: t.dim, fontSize: 16 }}>›</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delegated (I'm grantor — who I've given access to) */}
      {delegated.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.dim, marginBottom: 8 }}>已授權管理者</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {delegated.map(v => {
              const perms = JSON.parse(v.permissions || "{}");
              const permCount = Object.values(perms).filter(Boolean).length;
              return (
                <div key={v.id} onClick={() => setSelectedVenue(v)} style={{
                  background: t.card, borderRadius: 10, padding: "14px 18px", cursor: "pointer",
                  border: `1px solid ${t.border}`, transition: "border-color 0.15s",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, background: t.successBg,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                    }}>🤝</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>
                          {v.manager_name || v.invited_email}
                        </span>
                        <span style={{ fontSize: 11, color: t.dim }}>{v.label ? `· ${v.label}` : ""}</span>
                      </div>
                      <div style={{ fontSize: 11, color: t.muted }}>
                        {v.manager_email} · {permCount} 項權限
                      </div>
                    </div>
                    <StatusBadge status={v.status} />
                    <span style={{ color: t.dim, fontSize: 16 }}>›</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Pending invites */}
      {pending.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.warning, marginBottom: 8 }}>待接受邀請</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pending.map(v => (
              <div key={v.id} style={{
                background: t.card, borderRadius: 10, padding: "14px 18px",
                border: `1px solid ${t.warning}33`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, background: t.warningBg,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                  }}>📩</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{v.invited_email}</div>
                    <div style={{ fontSize: 11, color: t.dim }}>{v.label || "待接受"}</div>
                  </div>
                  <StatusBadge status="pending" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {allVenues.length === 0 && pending.length === 0 && (
        <div style={{
          padding: 60, textAlign: "center", background: t.card, borderRadius: 12,
          border: `1px solid ${t.border}`,
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: t.text }}>尚無場域管理關係</div>
          <div style={{ fontSize: 13, color: t.muted, marginTop: 4, marginBottom: 20 }}>
            邀請合作夥伴管理您的場域設備，或由場域業主授權給您
          </div>
          <button onClick={() => setShowInvite(true)} style={{
            padding: "10px 24px", borderRadius: 10, border: "none",
            background: t.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>+ 邀請第一位管理者</button>
        </div>
      )}
    </div>
  );
}

function Header() {
  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: t.text, margin: 0 }}>
        🏢 場域管理
      </h2>
      <div style={{ fontSize: 13, color: t.muted, marginTop: 4 }}>
        多場域委託管理 · 設備監控 · 收益查看
      </div>
    </div>
  );
}
